/**
 * Tenant theming - the curated, allowlisted set of design tokens a tenant can
 * override, plus the runtime helper that injects those overrides onto the
 * document root.
 *
 * The platform-wide defaults live in `frontend/src/shared/index.css` (the
 * "Clara Vale" baseline). A tenant theme is a *sparse* map: it stores only the
 * tokens the tenant has changed. Unset tokens fall through to the stylesheet,
 * which is what makes "the current values are the default for every tenant
 * until updated" true without copying the baseline into every tenant's data.
 *
 * The curated base tokens are chosen deliberately: `index.css` already cascades
 * them into the shadcn layer (`--primary: var(--green)`, `--background:
 * var(--bg)`, `--foreground: var(--ink)`, ...), so overriding the base token
 * propagates everywhere - buttons, surfaces, text - without exposing dozens of
 * derived tokens in the editor.
 *
 * Keep `THEME_TOKENS` in sync with `THEME_TOKEN_KEYS` in
 * `backend/apps/organisations/branding.py` (the server-side allowlist).
 */

import { loadGoogleFont } from './fonts'

export type ThemeTokenType = 'color' | 'font'

export interface ThemeTokenDef {
  /** The CSS custom property name, e.g. `--green`. Also the storage key. */
  key: string
  /** Human label shown in the editor. */
  label: string
  /** Short helper text describing where the token shows up. */
  hint: string
  /** Grouping for the editor layout. */
  group: 'Brand' | 'Surface' | 'Text' | 'Typography'
  type: ThemeTokenType
  /**
   * The platform baseline value, mirrored from `index.css`. Used to seed the
   * editor form and to render swatches; never written to storage unless the
   * tenant deliberately picks it. An empty string means "no value" (the token
   * is unset by default and falls through to per-component defaults).
   */
  default: string
}

/**
 * Font families offered in the editor live in `fonts.ts`: the platform-bundled
 * defaults (`BUNDLED_FONT_OPTIONS`) pinned on top, plus a curated list of common
 * Google Fonts (`CURATED_GOOGLE_FONTS`) loaded at runtime on selection.
 */

export const THEME_TOKENS: ThemeTokenDef[] = [
  // Brand
  {
    key: '--brand',
    label: 'Brand colour',
    hint: 'Brand-focus surfaces - login backgrounds and the welcome-page hero.',
    group: 'Brand',
    type: 'color',
    default: '#1c5a48',
  },
  {
    key: '--green',
    label: 'Accent colour',
    hint: 'Interactive elements - buttons, form controls, links, hover, selected and badge states.',
    group: 'Brand',
    type: 'color',
    default: '#1c5a48',
  },
  {
    key: '--gold',
    label: 'Accent (gold)',
    hint: 'Achievement / completed accents.',
    group: 'Brand',
    type: 'color',
    default: '#c5a94c',
  },
  // Surface
  {
    key: '--bg',
    label: 'Background',
    hint: 'Page background.',
    group: 'Surface',
    type: 'color',
    default: '#faf9f7',
  },
  {
    key: '--bg-subtle',
    label: 'Subtle background',
    hint: 'Cards, wells, secondary surfaces.',
    group: 'Surface',
    type: 'color',
    default: '#f5f3ef',
  },
  // Text
  {
    key: '--ink',
    label: 'Text (primary)',
    hint: 'Headings and primary copy.',
    group: 'Text',
    type: 'color',
    default: '#1a1a18',
  },
  {
    key: '--ink-2',
    label: 'Text (secondary)',
    hint: 'Body and secondary copy.',
    group: 'Text',
    type: 'color',
    default: '#4a4a46',
  },
  {
    key: '--ink-3',
    label: 'Text (muted)',
    hint: 'Captions, placeholders, hints.',
    group: 'Text',
    type: 'color',
    default: '#696966',
  },
  // Typography
  {
    key: '--font-display',
    label: 'Display font',
    hint: 'Headings and editorial titles.',
    group: 'Typography',
    type: 'font',
    default: '"Cormorant Garamond", Georgia, serif',
  },
  {
    key: '--font-sans',
    label: 'Body font',
    hint: 'Body text and UI.',
    group: 'Typography',
    type: 'font',
    default: '"Geist Variable", ui-sans-serif, system-ui, -apple-system, sans-serif',
  },
]

export const THEME_TOKEN_KEYS: ReadonlySet<string> = new Set(
  THEME_TOKENS.map((t) => t.key)
)

export type ThemeOverrides = Record<string, string>

/** Strip any non-allowlisted / empty entries from a raw theme map. */
export function sanitizeTheme(raw: unknown): ThemeOverrides {
  if (!raw || typeof raw !== 'object') return {}
  const out: ThemeOverrides = {}
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (THEME_TOKEN_KEYS.has(key) && typeof value === 'string' && value.trim()) {
      out[key] = value
    }
  }
  return out
}

/**
 * Resolve the brand (`--brand`) and accent (`--green`) colours from a tenant's
 * theme overrides, with mirroring: when only one is set the other follows it.
 *
 * Theming is opt-in: it is driven ONLY by explicit overrides authored via the
 * theme editor (`Tenant.settings["theme"]`). It deliberately does NOT fall back
 * to `Tenant.primary_colour` - that legacy field is set on many tenants and
 * using it as a seed would silently theme tenants who never opted in. A tenant
 * with no override returns `{}` here and inherits the platform (iWarranty)
 * theme from `index.css`.
 */
export function resolveBrandAccent(overrides: ThemeOverrides): {
  brand?: string
  accent?: string
} {
  const accentOverride = overrides['--green']
  const brandOverride = overrides['--brand']
  return {
    accent: accentOverride || brandOverride,
    brand: brandOverride || accentOverride,
  }
}

/**
 * Build the theme override map a page should apply from a tenant's authored
 * theme. Only explicit overrides theme the UI (see `resolveBrandAccent`); a
 * tenant with no theme override yields an empty map and inherits the platform
 * theme. The accent's derived shades (`--green-hover`, `--green-bg`, ...) are
 * computed from `--green` in `index.css` via color-mix; `--brand` drives the
 * brand-focus surfaces. All other overrides are carried through unchanged.
 */
export function brandingToTheme(opts: { theme?: unknown }): ThemeOverrides {
  const merged = sanitizeTheme(opts.theme)
  const { accent, brand } = resolveBrandAccent(merged)
  if (accent) merged['--green'] = accent
  if (brand) merged['--brand'] = brand
  return merged
}

/**
 * The per-token baseline the editor displays before any override: the platform
 * (Clara Vale / iWarranty) defaults. The editor shows these until a token is
 * explicitly overridden, matching what an un-themed tenant actually renders.
 */
export function themeBaseline(): ThemeOverrides {
  const base: ThemeOverrides = {}
  for (const token of THEME_TOKENS) base[token.key] = token.default
  return base
}

/**
 * Apply a tenant theme by setting the allowlisted custom properties on
 * `:root`. Tokens absent from `theme` are *cleared* (removed), so switching
 * org/tenant never leaves a stale override behind - the baseline in
 * `index.css` then takes over again.
 *
 * Idempotent and safe to call on every render of the provider.
 */
export function applyTenantTheme(theme: ThemeOverrides): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  const clean = sanitizeTheme(theme)
  for (const def of THEME_TOKENS) {
    const value = clean[def.key]
    if (value) {
      root.style.setProperty(def.key, value)
      // A tenant may pick a Google font that isn't bundled; fetch it so the
      // override actually renders (no-op for system/bundled families).
      if (def.type === 'font') loadGoogleFont(value)
    } else {
      root.style.removeProperty(def.key)
    }
  }
}
